import { createFileRoute } from "@tanstack/react-router";
import type { BriefingPrompt } from "@veritab/api-contracts";
import { BookOpen, Plus, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import {
  InstructionCard,
  InstructionFormDialog,
  useBriefingPromptsQuery,
  useDeactivateBriefingPromptMutation,
  useGenerateBriefingMutation,
} from "@/features/briefings";
import { usePageHeader } from "@/shared/store/page-header";
import { Button } from "@/shared/ui";

export const Route = createFileRoute("/_authenticated/briefings/instructions")({
  component: InstructionsPage,
});

function InstructionsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<BriefingPrompt | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const { setTitle, reset } = usePageHeader();

  useEffect(() => {
    setTitle("Briefing Instructions");
    return () => reset();
  }, [setTitle, reset]);

  const { data, isLoading } = useBriefingPromptsQuery();
  const generateMutation = useGenerateBriefingMutation();
  const deactivateMutation = useDeactivateBriefingPromptMutation();

  const prompts = data?.prompts ?? [];

  const handleGenerate = async (promptId: string, deliverToSlack: boolean) => {
    setGeneratingId(promptId);
    try {
      await generateMutation.mutateAsync({ promptId, deliverToSlack });
    } finally {
      setGeneratingId(null);
    }
  };

  const handleEdit = (instruction: BriefingPrompt) => {
    setEditingInstruction(instruction);
    setDialogOpen(true);
  };

  const handleDeactivate = (id: string) => {
    deactivateMutation.mutate(id);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) setEditingInstruction(null);
  };

  return (
    <div className="p-4 sm:p-8 space-y-8 max-w-4xl">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Briefing Instructions
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground/70">
            Create and manage instructions for generating briefings
          </p>
        </div>
        <Button onClick={() => { setEditingInstruction(null); setDialogOpen(true); }} className="rounded-lg shadow-sm">
          <Plus className="mr-1.5 h-4 w-4" />
          New Instruction
        </Button>
      </div>

      {/* Separator */}
      <div className="h-px bg-gradient-to-r from-border/60 via-border/30 to-transparent" />

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[140px] rounded-xl border border-border/40 bg-muted/10 animate-pulse"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      ) : prompts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          {/* bg-muted/text-primary here (not opacity variants of the same
              --primary token) so the icon stays visible even on WebViews
              without color-mix() support, where Tailwind's opacity
              modifiers fall back to full-opacity color — two shades of the
              same token would otherwise collapse into one solid block. */}
          <div className="relative mb-6">
            <div className="rounded-2xl bg-muted p-5">
              <BookOpen className="h-8 w-8 text-primary" />
            </div>
            <div className="absolute -top-1 -right-1 rounded-full bg-muted p-1">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
            </div>
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1.5">No instructions yet</h3>
          <p className="text-sm text-muted-foreground/60 mb-6 max-w-xs leading-relaxed">
            Create your first briefing instruction to start generating analysis reports.
          </p>
          <Button onClick={() => { setEditingInstruction(null); setDialogOpen(true); }} className="rounded-lg shadow-sm">
            <Plus className="mr-1.5 h-4 w-4" />
            New Instruction
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {prompts.map((prompt, i) => (
            <div
              key={prompt.id}
              className="animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: `${i * 60}ms`, animationFillMode: "backwards" }}
            >
              <InstructionCard
                instruction={prompt}
                isGenerating={generatingId === prompt.id}
                onGenerate={handleGenerate}
                onEdit={handleEdit}
                onDeactivate={handleDeactivate}
              />
            </div>
          ))}
        </div>
      )}

      <InstructionFormDialog
        key={editingInstruction?.id ?? "new"}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        editingInstruction={editingInstruction}
      />
    </div>
  );
}
